import * as React from 'react'
import { View, StyleSheet, Pressable, StyleProp, ViewStyle } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'
import { useTheme } from '../../hooks/theme.hook'
import { Svg, Path, G, ClipPath, Defs, Rect } from 'react-native-svg'
import { EyeIcon } from '../../utils/meldd-icons'

interface EyeToggleSwitchProps {
  value: boolean
  onValueChange: (value: boolean) => void
  style?: StyleProp<ViewStyle>
  disabled?: boolean
}

export function EyeToggleSwitch({
  value,
  onValueChange,
  style,
  disabled = false,
}: EyeToggleSwitchProps) {
  const theme = useTheme()
  const translateX = useSharedValue(value ? 1 : 0)

  React.useEffect(() => {
    translateX.value = withSpring(value ? 1 : 0, {
      damping: 15,
      stiffness: 150,
    })
  }, [value, translateX])

  const animatedThumbStyle = useAnimatedStyle(() => {
    const TRACK_WIDTH = 52
    const THUMB_SIZE = 28
    const PADDING = 2
    const maxTranslate = TRACK_WIDTH - THUMB_SIZE - PADDING * 2

    return {
      transform: [{ translateX: translateX.value * maxTranslate }],
    }
  })

  const animatedTrackStyle = useAnimatedStyle(() => {
    const activeColor = theme.colors.primary || '#4259AA'
    const inactiveColor = theme.colors.surfaceVariant || '#C6C5D0'

    return {
      backgroundColor: value ? activeColor : inactiveColor,
    }
  })

  const handlePress = () => {
    if (!disabled) {
      onValueChange(!value)
    }
  }

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      style={[styles.container, style]}
    >
      <Animated.View style={[styles.track, animatedTrackStyle]}>
        <Animated.View style={[styles.thumb, animatedThumbStyle]}>
          <View style={styles.eyeIconContainer}>
            <EyeIcon size={16} color={theme.colors.primary} />
          </View>
        </Animated.View>
      </Animated.View>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  track: {
    width: 52,
    height: 32,
    borderRadius: 16,
    padding: 2,
    justifyContent: 'center',
  },
  thumb: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  eyeIconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
})
